import * as React from 'react';
import { produce } from 'immer';
import { nanoid } from 'nanoid';
import apptracker from '@utils/sensor/apptracker';
import * as Events from '@src/utils/sensor/track';
import * as Store from '../store';
import { checkEmptyRule } from './utils';
import SavedFilterAtom from './SavedFilterAtom';
import RuleAtom, { type RuleAtomProps, type RuleAtomChange } from './RuleAtom';
import type { FilterValue, FilterRule, FilterRuleValuePair } from '../../../types';

export interface RulesProps {
  showValidation?: boolean;
}

const hashSymbol = Symbol('STANDARD_RULE_HASH');
const modifyFlagSymbol = Symbol('STANDARD_RULE_MODIFY_FLAG');

type StandardRuleKey = Pick<FilterRule, 'op' | 'field' | 'key'> & {
  [hashSymbol]: string;
  [modifyFlagSymbol]?: string;
};

type StandardRuleValue = FilterRuleValuePair & {
  [hashSymbol]: string;
};

type StandardRuleItem = StandardRuleKey & {
  rules: StandardRuleValue[];
};

type FilterRuleWithModifyFlag = FilterRule & {
  [modifyFlagSymbol]?: string;
};

function getKeyHash(op: string, field: string, key?: string | null, modifyHash?: string) {
  return `${field}_${field === 'TAG' ? key : ''}_${op}_${modifyHash ?? ''}`;
}

function getValueHash(display: string, value: string | number) {
  return `${display}_${value}`;
}

function getRulesHash(keyHash: string, valueHashes: string[]) {
  return `${keyHash}@${valueHashes.join('+')}`;
}

/**
 * A standardized function for processing filter rules.
 * According to UX design, items with the same dimension and the same operator in the same rule group
 * need to be displayed in an Atom component.
 *
 * This function generates a hash based on the rule value (op, field, key, value, display),
 * then deduplicates and buckets the rules, and finally assembles them into a two-dimensional array with StandardRuleValue.
 */
function getStandardFilterValue(value: FilterValue): StandardRuleItem[][] {
  return (value.rule as FilterRuleWithModifyFlag[][]).map((rules) => {
    const standardKeyMap = new Map<string, StandardRuleKey>();
    const standardValueMap = new Map<string, StandardRuleValue>();
    const standardKeyValueHashMap = new Map<string, Set<string>>();

    rules.forEach((it) => {
      const originalKeyHash = getKeyHash(it.op, it.field, it.key, it[modifyFlagSymbol]);
      const valueHash = getValueHash(it.display, it.value);
      const keyHash = it.op.startsWith('not_')
        ? getRulesHash(originalKeyHash, [valueHash])
        : originalKeyHash;

      standardKeyMap.set(keyHash, {
        op: it.op,
        field: it.field,
        key: it.field === 'TAG' ? it.key : null,
        [hashSymbol]: keyHash,
        [modifyFlagSymbol]: it[modifyFlagSymbol],
      });
      standardValueMap.set(valueHash, {
        display: it.display,
        value: it.value,
        [hashSymbol]: valueHash,
      });

      if (!standardKeyValueHashMap.has(keyHash)) {
        standardKeyValueHashMap.set(keyHash, new Set());
      }

      standardKeyValueHashMap.get(keyHash)!.add(valueHash);
    });

    return Array.from(standardKeyValueHashMap.entries()).map(([k, v]) => {
      const key = standardKeyMap.get(k)!;
      const values = Array.from(v).map((it) => standardValueMap.get(it)!);
      const hash = getRulesHash(
        key[hashSymbol],
        values.map((it) => it[hashSymbol]),
      );

      return {
        ...key,
        rules: values,
        [hashSymbol]: hash,
        [modifyFlagSymbol]: key[modifyFlagSymbol],
      };
    });
  });
}

function getFilterRuleFromStandard(value: StandardRuleItem[][]) {
  return value
    .map((group) => {
      return group.reduce((acc, ruleSet) => {
        acc.push(
          ...ruleSet.rules.map((it) => ({
            field: ruleSet.field,
            key: ruleSet.key,
            op: ruleSet.op,
            display: it.display,
            value: it.value,
            [modifyFlagSymbol]: ruleSet[modifyFlagSymbol],
          })),
        );
        return acc;
      }, [] as FilterRuleWithModifyFlag[]);
    })
    .filter((it) => it.length);
}

function removeFromRules(value: StandardRuleItem[][], path: [number, number]): FilterRule[][] {
  const next = produce(value, (draft) => {
    const [groupIndex, ruleSetIndex] = path;

    draft[groupIndex].splice(ruleSetIndex, 1);
  });

  return getFilterRuleFromStandard(next);
}

function editFromRules(
  value: StandardRuleItem[][],
  path: [number, number],
  change: RuleAtomChange,
): FilterRuleWithModifyFlag[][] {
  const [groupIndex, ruleSetIndex] = path;
  const nextValue = produce(value, (draft) => {
    const target = draft[groupIndex][ruleSetIndex];
    const noFieldOpChanged =
      getKeyHash(target.op, target.field, target.key) ===
      getKeyHash(change.op, change.field, change.tagKey);

    // Update the rule values only if the field and operator are not changed
    if (noFieldOpChanged) {
      target.rules = change.values.map((it) => ({
        display: it.display,
        value: it.value,
        [hashSymbol]: ``,
      }));
      target[modifyFlagSymbol] = nanoid();

      return;
    }

    // Remove the current rule
    draft[groupIndex].splice(ruleSetIndex, 1);

    // If the operator is changed to `not_`, the current rule should be removed and split its values into separate rules
    if (!target.op.startsWith('not_') && change.op.startsWith('not_') && change.values.length > 1) {
      draft.splice(
        groupIndex,
        0,
        ...change.values.map((it) => [
          {
            op: change.op,
            field: change.field,
            key: change.tagKey,
            rules: [
              {
                ...it,
                [hashSymbol]: ``,
              },
            ],
            [hashSymbol]: ``,
            [modifyFlagSymbol]: nanoid(),
          },
        ]),
      );

      return;
    }

    // Insert the new rule
    draft[groupIndex].splice(ruleSetIndex, 0, {
      op: change.op,
      field: change.field,
      key: change.tagKey,
      rules: change.values.map((it) => ({
        display: it.display,
        value: it.value,
        [hashSymbol]: ``,
      })),
      [hashSymbol]: ``,
      [modifyFlagSymbol]: nanoid(),
    });
  });

  return getFilterRuleFromStandard(nextValue);
}

function triggerChangeEventTrack(change: RuleAtomChange, original: StandardRuleItem) {
  let event = Events.FILTER_BUILDER_CHANGE_CONDITION_VALUES_ON_SIMPLE_MODE;
  const payload = {
    field: change.field === 'TAG' ? change.tagKey : change.field,
  } as Record<string, any>;

  if (original.op !== change.op) {
    event = Events.FILTER_BUILDER_CHANGE_CONDITION_OPERATOR_ON_SIMPLE_MODE;
    payload.from = original.op;
    payload.to = change.op;
  } else if (original.field !== change.field) {
    event = Events.FILTER_BUILDER_CHANGE_CONDITION_DIMENSION_ON_SIMPLE_MODE;
    payload.from = original.field;
    payload.to = change.field;
  } else {
    payload.from = original.rules.map((it) => it.value).join(',');
    payload.to = change.values.map((it) => it.value).join(',');
  }

  apptracker(event, payload);
}

const Rules = (props: RulesProps) => {
  const [filterValue, setFilterValue] = Store.useFilterValue();
  const [filterStatus] = Store.useFilterStatus();
  const [conditionExpanded] = Store.useRulesExpanded();
  const onChange = Store.useOnChange();
  const standardFilterValue = React.useMemo(
    () => getStandardFilterValue(filterValue),
    [filterValue],
  );

  const filterStatusRef = React.useRef(filterStatus);
  const standardFilterValueRef = React.useRef(standardFilterValue);
  const onChangeRef = React.useRef(onChange);
  filterStatusRef.current = filterStatus;
  standardFilterValueRef.current = standardFilterValue;
  onChangeRef.current = onChange;

  const handleAtomRemove = React.useCallback(
    (path: [number, number]) => {
      const originalValue = standardFilterValueRef.current[path[0]][path[1]];

      apptracker(Events.FILTER_BUILDER_REMOVE_CONDITION_ITEM_ON_SIMPLE_MODE, {
        editing: filterStatusRef.current === 'editing',
        value: JSON.stringify(originalValue),
      });

      const nextRules = removeFromRules(standardFilterValueRef.current, path);

      // There are only two cases where the filter value atom should be removed: editing or idle
      if (filterStatusRef.current === 'editing') {
        setFilterValue({
          rule: nextRules,
        });

        return;
      }

      onChangeRef.current?.({
        rule: nextRules,
      });
    },
    [setFilterValue],
  );
  const handleAtomChange = React.useCallback<NonNullable<RuleAtomProps['onChange']>>(
    (value, path) => {
      const originalValue = standardFilterValueRef.current[path[0]][path[1]];
      triggerChangeEventTrack(value, originalValue);

      const nextRules = editFromRules(standardFilterValueRef.current, path, value);
      setFilterValue({
        rule: nextRules,
      });
    },
    [setFilterValue],
  );

  if (!!filterValue.fid && filterStatus !== 'editing') {
    return <SavedFilterAtom />;
  }

  return (
    <>
      {standardFilterValue.map((group, groupIndex) => (
        <React.Fragment key={groupIndex}>
          {group.map((ruleSet, ruleSetIndex) => {
            return (
              <RuleAtom
                key={ruleSet[hashSymbol]}
                forceActive={checkEmptyRule(ruleSet.rules) || !!ruleSet[modifyFlagSymbol]}
                forceExpand={conditionExpanded}
                defaultActive={
                  typeof filterValue.fid === 'number' &&
                  standardFilterValue.length === 1 &&
                  group.length === 1
                }
                showValidation={props.showValidation}
                path={[groupIndex, ruleSetIndex] as const}
                notOpDisabled={group.length > 1 && ruleSet.rules.length > 1}
                op={ruleSet.op}
                field={ruleSet.field}
                tagKey={ruleSet.key}
                values={ruleSet.rules}
                onRemove={handleAtomRemove}
                onChange={handleAtomChange}
              />
            );
          })}
        </React.Fragment>
      ))}
    </>
  );
};

export default React.memo(Rules);
